import { defineStore } from 'pinia'
import type Fleet from '@/types/Fleet.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import Eta from '@/models/eta.ts'
import { logger } from '@/models/logger';

export const useFleetStore = defineStore('fleetStore', {
  state: () => ({
    fleets: new Map<number, Fleet>(),
    updateVersion: 0  // Increments on any fleet change for efficient watching
  }),
  getters: {
    findFleetById:
      (state) =>
      (id: number): Fleet | undefined => {
        return state.fleets.get(id)
      }
  },
  actions: {
    updateFleetById(id: number, data: Fleet) {
      data = this.getFleetStats(data)

      this.fleets.set(id, data)
      this.updateVersion++;
    },
    refreshFleetData(id: number) {
      const fleet = this.fleets.get(id)
      if (fleet) {
        this.updateFleetById(id, fleet)
      }
    },
    deleteFleetById(id: number) {
      if (this.fleets.has(id)) {
        this.fleets.delete(id)
        this.updateVersion++;
      }
    },
    getFleetNameById(id: number) {
      if (id > 0 && this.fleets.has(id)) {
        const fleet = this.fleets.get(id)
        return fleet?.name ?? 'No name'
      }
      logger.debug(this.fleets)
      return 'No fleet'
    },
    getFleetStats(data: Fleet) {
      const shipStore = useShipStore()
      const ships = shipStore.getShipsFromFleet(data.id)
      let maxSpeed = 0
      let nrOfShips = 0
      let attackShips = 0
      let attackPlanets = 0
      let shields = 0
      let scanningRange = 0
      let maxHealth = 0
      let currentHealth = 0
      let healthPercentage = 100
      let repair = 0
      for (const ship of ships) {
        nrOfShips++
        //Speed - find the slowest ship (minimum speed)
        if (maxSpeed === 0 || ship.maxSpeed < maxSpeed) {
          if (!ship.catchingUpToFleet) {
            maxSpeed = ship.maxSpeed
          }
        }
        //Attack
        attackShips += ship.attackShips
        attackPlanets += ship.attackPlanets
        shields += ship.shields
        //Scanning Range
        if (ship.scanningRange > scanningRange) {
          scanningRange = ship.scanningRange
        }
        //Health
        maxHealth += ship.maxHitPoints
        currentHealth += ship.currentHitPoints
        repair += ship.repair
      }

      if (nrOfShips > 0) {
        //Health percentage
        healthPercentage =
          currentHealth === maxHealth ? 100 : Math.floor(currentHealth / (maxHealth / 100))
      }

      data.nrOfShips = nrOfShips
      data.maxSpeed = maxSpeed
      data.attackShips = attackShips
      data.attackPlanets = attackPlanets
      data.shields = shields
      data.scanningRange = scanningRange
      data.healthPercentage = healthPercentage
      data.repair = repair

      // Calculate the eta to the next hop
      if (data.actions) {
        let etaNextHop = 0
        if (data.actions.length > 0) {
          const action = data.actions[0]
          // For repair actions, use duration instead of movement calculation
          if (action.action === 'repair' && action.duration !== undefined) {
            etaNextHop = action.duration === 1000 ? 0 : action.duration
          } else {
            const eta = new Eta()
            eta.start(data.xPos, data.yPos)
            etaNextHop = eta.etaNext(action.targetX, action.targetY, data.maxSpeed)
          }
        }
        data.etaNextHop = etaNextHop
      } else {
        data.etaNextHop = 0
      }

      return data
    }
  }
})
